import React, { useContext, useState } from "react";

import classNames from "classnames";
import { motion } from "framer-motion";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import styled from "styled-components";

import SocialMediaLinks from "../../../components/Footer/SocialMediaLinks";
import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";
import FlexibleImage from "../FlexibleImage/FlexibleImage";
import Map from "../Map/Map";

const AnimatedContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${(props) => props.dynamicStyles || ""}
`;

const PlainContainer = styled.div`
  ${(props) => props.dynamicStyles || ""}
`;

const BackgroundAnimationWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
`;

function AnimationProvider({
  children,
  backgroundAnimations,
  rowClassName,
  dynamicStyles,
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <AnimatedContainer
      className={rowClassName}
      dynamicStyles={dynamicStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <BackgroundAnimationWrapper>
        {backgroundAnimations.map((anim, index) => {
          const variants = {
            initial: anim.animationConfig.initial,
            hover: anim.animationConfig.hover,
          };

          return (
            <motion.div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={anim.animationConfig.style}
              variants={variants}
              initial="initial"
              animate={isHovered ? "hover" : "initial"}
              transition={anim.animationConfig.transition}
            >
              <img
                src={anim?.image?.url}
                style={anim.animationConfig.imgConfig}
                alt={anim?.image?.alternativeText}
              />
            </motion.div>
          );
        })}
      </BackgroundAnimationWrapper>
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </AnimatedContainer>
  );
}

function TextWith() {
  const {
    text,
    elementColumns,
    textColumns,
    textColumnGap,
    elementColumnGap,
    elementColumnClassName,
    reverse,
    textColumnClassName,
    CSS,
    fullWidth,
    backgroundAnimations,
  } = useContext(ComponentContext);

  const textColumnWrapperClassName = classNames(
    `col-md-${textColumns} offset-md-${textColumnGap}`
  );
  const elementColumnWrapperClassName = classNames(
    `col-md-${elementColumns} offset-md-${elementColumnGap}`
  );

  const rowClassName = classNames(
    { row: !fullWidth },
    { "flex-row-reverse": reverse }
  );
  const element = Object.entries(useContext(ComponentContext)).find(
    ([key, value]) => ["map", "socialMedia", "imageComp"].includes(key) && value
  );
  let elementName;
  let elementValues;
  if (!isEmpty(element)) {
    [elementName, elementValues] = element;
  }
  const elementToComponentMap = {
    imageComp: FlexibleImage,
    map: Map,
    socialMedia: SocialMediaLinks,
  };
  const Element = elementToComponentMap[elementName];
  const isElementAdded = elementName && !isEmpty(elementValues);

  const hasAnimations = !isEmpty(backgroundAnimations);

  const content = (
    <>
      <div className={textColumnWrapperClassName}>
        <div className={textColumnClassName}>
          <Markdown>{text}</Markdown>
        </div>
      </div>
      {isElementAdded && (
        <div className={elementColumnWrapperClassName}>
          <ComponentContext.Provider value={elementValues}>
            <div className={elementColumnClassName}>
              <Element />
            </div>
          </ComponentContext.Provider>
        </div>
      )}
    </>
  );

  if (hasAnimations) {
    return (
      <AnimationProvider
        backgroundAnimations={backgroundAnimations}
        rowClassName={rowClassName}
        dynamicStyles={CSS}
      >
        {content}
      </AnimationProvider>
    );
  }

  return (
    <PlainContainer className={rowClassName} dynamicStyles={CSS}>
      {content}
    </PlainContainer>
  );
}

export default observer(TextWith);
